import { useEffect, useRef, useState, useCallback } from 'react'
import { api, type Painel, type Delta, type Pedido, type Representante } from '../api'
import { rankingMudou } from '../ranking'

/**
 * O estado do painel, vindo do `GET /api/painel` e mantido atualizado pelo SSE.
 *
 * Três coisas que este gancho resolve e que não são obvias:
 *
 *  - **A lista de pedidos não é redesenhada.** O delta traz só `pedidosNovos`, e
 *    eles entram NA FRENTE de uma lista imutável. Quem já estava fica sendo o
 *    mesmo objeto, então com `key` pelo número do pedido o React não recria
 *    aquele nó — é o que faz a lista crescer sem piscar.
 *  - **Nunca mostrar zero por falha.** Se o SSE cai ou a fonte morre, o estado
 *    anterior PERMANECE. O que muda é a idade do snapshot, e é ela que acende o
 *    aviso. Zerar o telão seria mentira em cima do número.
 *  - **Dois eventos, não um.** O `snapshot` é a coleta e traz `vendido`; o
 *    `cadastro` é alguém salvando no admin e não traz nada — ele faz o telão
 *    refazer o `GET /api/painel`, que é de onde meta, submeta, logo e rotação
 *    sempre vieram.
 */

const TETO_PEDIDOS_MEMORIA = 60   // o que cabe na tela; o resto é passado

/**
 * Freio do aviso de cadastro, em ms.
 *
 * "Salvar marcas" no admin é UM clique e várias requisições: o `PUT
 * /api/admin/divisoes` mais um `POST /api/admin/logo/:id` por logo trocada.
 * Cada uma avisa, e sem freio o telão faria uma consulta por requisição do
 * lote. Juntando-as numa só, o clique vira uma recarga.
 *
 * 400 ms é curto a ponto de ninguém perceber (o pedido era 2 s) e longo o
 * bastante para cobrir o lote, que sai em sequência do mesmo navegador.
 */
const FREIO_DO_CADASTRO_MS = 400

type Estado = {
  painel: Painel | null
  /** Pedidos por divisão, do mais recente para o mais antigo. */
  pedidos: Record<string, Pedido[]>
  /**
   * Ranking de representante por divisão, já na ordem do servidor.
   *
   * SUBSTITUÍDO a cada delta que o traz, nunca mesclado como os pedidos: é um
   * retrato de 15 linhas, e mesclar manteria na tela quem caiu do pódio.
   */
  representantes: Record<string, Representante[]>
  /** `true` quando a conexão SSE está de pé. */
  conectado: boolean
  /** Segundos desde a última coleta. É o que acende o aviso de fonte caída. */
  idadeSeg: number
  erro: string | null
}

/** Acrescenta os novos na frente, sem recriar os que já estavam. */
function acrescentar(atuais: Pedido[], novos: Pedido[]): Pedido[] {
  if (!novos.length) return atuais
  const jaTem = new Set(atuais.map(p => p.numero))
  const frescos = novos.filter(p => !jaTem.has(p.numero))
  if (!frescos.length) return atuais
  return [...frescos, ...atuais].slice(0, TETO_PEDIDOS_MEMORIA)
}

/** O instante de um ISO, ou `null` quando não dá para ler. */
function instanteDe(iso: string | null | undefined): number | null {
  if (typeof iso !== 'string' || iso === '') return null
  const valor = Date.parse(iso)
  return Number.isNaN(valor) ? null : valor
}

function aplicarDelta(estado: Estado, delta: Delta): Estado {
  if (!estado.painel) return estado

  /* Snapshot mais velho que o que já está na tela não retrocede o número. Com
     retentativa do coletor, um POST atrasado pode chegar depois do seguinte.

     A comparação é por INSTANTE, não por texto: o servidor aceita qualquer ISO
     8601 com data e hora (`FORMA_DO_INSTANTE`, em `rotas/ingestao.js`),
     inclusive com fuso `-03:00`. No `<` de string, um `-03:00` contra um `Z`
     ordena pelo caractere e o sintoma seria telão congelado no último número. */
  const naTela = instanteDe(estado.painel.coletadoEm)
  const chegando = instanteDe(delta.coletadoEm)
  if (naTela !== null && chegando !== null && chegando < naTela) {
    return estado
  }

  const porId = new Map(delta.divisoes.map(d => [d.id, d]))
  const divisoes = estado.painel.divisoes.map(divisao => {
    const nova = porId.get(divisao.id)
    /* Divisão ausente do delta é "não coletei", não "zerou": mantém o valor. */
    if (!nova) return divisao

    const vendidoPorCodigo = new Map(nova.subitens.map(s => [s.codigo, s.vendido]))
    return {
      ...divisao,
      vendido: nova.vendido,
      subitens: divisao.subitens.map(s =>
        vendidoPorCodigo.has(s.codigo)
          ? { ...s, vendido: vendidoPorCodigo.get(s.codigo)! }
          : s,
      ),
      /* O PÓDIO acompanha o delta, e não só o `/api/painel` do carregamento.
         Sem isto, uma página aberta antes do primeiro ranking ficava com o
         pódio vazio para sempre: foi o que esvaziou a comemoração do sexto
         milhão em 13/10, porque o painel reiniciou no deploy minutos antes e a
         tela carregou com as listas ainda vazias.
         Delta sem `representantes` é "não mudou" e preserva o que está. */
      topRepresentantes: nova.representantes
        ? nova.representantes.slice(0, 3).map(r => ({
            posicao: r.posicao,
            nome: r.nome,
            fantasia: r.fantasia,
            nomePessoal: r.nomePessoal,
            valor: r.valor,
          }))
        : divisao.topRepresentantes,
    }
  })

  const pedidos = { ...estado.pedidos }
  for (const d of delta.divisoes) {
    if (d.pedidosNovos?.length) {
      pedidos[d.id] = acrescentar(pedidos[d.id] ?? [], d.pedidosNovos)
    }
  }

  /* O ranking SUBSTITUI, e só quando o delta o traz: campo ausente é "não
     mudou" ou "esta fonte não mede ranking", e nos dois casos o que está na
     tela continua valendo. O `rankingMudou` evita trocar o array por um igual —
     array novo invalidaria o `memo` das 15 linhas sem nada ter mudado. */
  let representantes = estado.representantes
  for (const d of delta.divisoes) {
    if (!d.representantes) continue
    const atuais = representantes[d.id] ?? []
    if (!rankingMudou(d.representantes, atuais)) continue
    if (representantes === estado.representantes) representantes = { ...representantes }
    representantes[d.id] = d.representantes
  }

  /* Soma SÓ o que foi medido, como faz o `GET /api/painel`: nada medido é
     `null`, não `0`. Somar `null` como zero devolveria "R$ 0" no número herói
     justamente quando ninguém mediu nada — o caso que o painel não pode exibir. */
  const medidos = divisoes.filter(d => typeof d.vendido === 'number')
  const vendidoTotal =
    medidos.length === 0 ? null : medidos.reduce((soma, d) => soma + (d.vendido ?? 0), 0)

  return {
    ...estado,
    painel: {
      ...estado.painel,
      divisoes,
      vendidoTotal,
      coletadoEm: delta.coletadoEm,
      fontes: delta.fontes ?? estado.painel.fontes,
      /* `demonstracao` TEM de atravessar o delta: o contrato manda o aviso de
         dado fictício sair sozinho no primeiro snapshot real. Sem isto ele
         ficava aceso sobre número verdadeiro pelos 4 dias do evento. */
      demonstracao: delta.demonstracao ?? estado.painel.demonstracao,
    },
    pedidos,
    representantes,
    idadeSeg: 0,
  }
}

export function usarFluxo() {
  const [estado, setEstado] = useState<Estado>({
    painel: null, pedidos: {}, representantes: {}, conectado: false, idadeSeg: 0, erro: null,
  })

  /* Em ref, não em estado: o relógio precisa ler isto a cada segundo sem que
     mudá-lo provoque repintura. */
  const coletadoEm = useRef<number | null>(null)

  const recarregar = useCallback(async () => {
    try {
      const painel = await api.painel()
      coletadoEm.current = instanteDe(painel.coletadoEm)
      setEstado(anterior => ({
        ...anterior, painel, idadeSeg: painel.idadeSeg, erro: null,
      }))
    } catch (e) {
      /* Falha de recarga NÃO apaga o que está na tela. O número velho com a
         hora da coleta serve; um painel em branco, não. */
      setEstado(anterior => ({
        ...anterior,
        erro: e instanceof Error ? e.message : 'falha ao consultar o painel',
      }))
    }
  }, [])

  useEffect(() => { void recarregar() }, [recarregar])

  /* ---------------------------------------------------------------------
     SSE, com reconexão.

     O `EventSource` reconecta sozinho, mas NÃO recompõe o que passou — então a
     cada reconexão o `GET /api/painel` refaz o estado. Sem isso, um blip de
     rede deixa o telão com o número de antes do blip pelo resto do evento.

     A limpeza do efeito fecha a conexão. Num painel que fica 4 dias ligado,
     vazar um EventSource por remontagem é acumular conexão no servidor até ele
     recusar — e o React, em modo estrito, monta duas vezes de propósito.
     --------------------------------------------------------------------- */
  useEffect(() => {
    let vivo = true
    let fonte: EventSource | null = null
    let reconectar: ReturnType<typeof setTimeout> | null = null
    let freioDoCadastro: ReturnType<typeof setTimeout> | null = null
    let primeiraAbertura = true

    const abrir = () => {
      if (!vivo) return
      fonte = new EventSource('/api/stream', { withCredentials: true })

      fonte.addEventListener('open', () => {
        if (!vivo) return
        setEstado(anterior => ({ ...anterior, conectado: true }))
        /* Reabertura: recompõe. Na primeira, o `recarregar` do efeito acima já
           fez isso — repetir aqui dobraria a consulta no boot de cada telão. */
        if (!primeiraAbertura) void recarregar()
        primeiraAbertura = false
      })

      fonte.addEventListener('snapshot', evento => {
        if (!vivo) return
        try {
          const delta = JSON.parse((evento as MessageEvent).data) as Delta
          /* Só sobrescreve com data legível: `Date.parse` de lixo devolve `NaN`,
             e aí a idade virava `NaN` — "atualizado há NaN s" no telão. */
          const quando = instanteDe(delta.coletadoEm)
          if (quando !== null) coletadoEm.current = quando
          setEstado(anterior => aplicarDelta(anterior, delta))
        } catch {
          /* Mensagem malformada é descartada em silêncio: ela não pode derrubar
             o laço de eventos nem apagar o último número bom. */
        }
      })

      /* ------------------------------------------------------------------
         CADASTRO MUDOU no admin — meta geral, meta de marca, submeta, nome,
         logo, rotação, filtro de faturamento.

         O evento NÃO traz o cadastro novo: ele diz que há cadastro novo, e o
         `GET /api/painel` o busca. É o mesmo caminho da reconexão acima, e por
         isso meta, submeta, logo e rotação chegam de uma vez, com a montagem
         vivendo num lugar só — o servidor.

         Sem isto, salvar uma meta no admin não aparecia no telão NUNCA sem F5:
         o `event: snapshot` da coleta carrega só `vendido`, e o cadastro era
         lido uma vez, no carregamento da página.
         ------------------------------------------------------------------ */
      fonte.addEventListener('cadastro', () => {
        if (!vivo) return
        /* Reinicia o freio a cada aviso do lote: "Salvar marcas" manda o PUT
           das divisões e um POST por logo, e o que interessa é recarregar UMA
           vez, depois do último. */
        if (freioDoCadastro !== null) clearTimeout(freioDoCadastro)
        freioDoCadastro = setTimeout(() => {
          freioDoCadastro = null
          if (!vivo) return
          void recarregar()
        }, FREIO_DO_CADASTRO_MS)
      })

      fonte.addEventListener('error', () => {
        if (!vivo) return
        setEstado(anterior => ({ ...anterior, conectado: false }))
        /* O EventSource tenta sozinho, mas a tentativa dele não tem teto nem
           espaçamento. Fechar e reabrir com 3 s de espera evita martelar o
           servidor quando ele é que está fora. */
        fonte?.close()
        fonte = null
        reconectar = setTimeout(abrir, 3000)
      })
    }

    abrir()
    return () => {
      vivo = false
      if (reconectar) clearTimeout(reconectar)
      /* O freio também: um aviso de cadastro chegando no instante do desmonte
         deixaria um `setTimeout` pendurado chamando `recarregar` em componente
         que saiu — e são quatro dias de reconexão. */
      if (freioDoCadastro) clearTimeout(freioDoCadastro)
      fonte?.close()
    }
  }, [recarregar])

  /* Relógio: a idade do snapshot sobe sozinha mesmo sem chegar nada. É assim
     que o painel descobre que a fonte caiu — não há evento de "não chegou". */
  useEffect(() => {
    const laco = setInterval(() => {
      setEstado(anterior => {
        const idade = coletadoEm.current === null
          ? anterior.idadeSeg
          : Math.round((Date.now() - coletadoEm.current) / 1000)
        return idade === anterior.idadeSeg ? anterior : { ...anterior, idadeSeg: idade }
      })
    }, 1000)
    return () => clearInterval(laco)
  }, [])

  /** Semeia a lista de uma divisão com o que o servidor tem (pré-carregamento). */
  const semearPedidos = useCallback((id: string, pedidos: Pedido[]) => {
    setEstado(anterior => {
      /* Se o SSE já trouxe pedido mais novo para esta divisão, não sobrescreve:
         mescla, para a semente não apagar o que chegou enquanto ela vinha. */
      const atuais = anterior.pedidos[id] ?? []
      const jaTem = new Set(pedidos.map(p => p.numero))
      const maisNovos = atuais.filter(p => !jaTem.has(p.numero))
      return {
        ...anterior,
        pedidos: { ...anterior.pedidos, [id]: [...maisNovos, ...pedidos].slice(0, TETO_PEDIDOS_MEMORIA) },
      }
    })
  }, [])

  /**
   * Semeia o ranking de uma divisão com o que o servidor tem.
   *
   * SOBRESCREVE, ao contrário de `semearPedidos`, que mescla: o ranking é um
   * retrato, e o do servidor é por definição o mais completo — ele saiu da
   * mesma leitura que deu os 15 lugares. Mesclar aqui misturaria duas ordens.
   *
   * Só não sobrescreve quando o conteúdo é igual, para não trocar o array e
   * invalidar o `memo` das linhas de graça.
   */
  const semearRanking = useCallback((id: string, representantes: Representante[]) => {
    setEstado(anterior => {
      const atuais = anterior.representantes[id] ?? []
      if (!rankingMudou(representantes, atuais)) return anterior
      return {
        ...anterior,
        representantes: { ...anterior.representantes, [id]: representantes },
      }
    })
  }, [])

  return { ...estado, recarregar, semearPedidos, semearRanking }
}
